import type {
	PermissionContextDto,
	RolePermissionsMatrixApiEntry,
	RolePermissionsMatrixApiResponse,
} from "@Front/Api/LeCoffreApi/SuperAdmin/RolePermissions/RolePermissions";
import { LoggerService } from "@Front/Services/LoggerService/LoggerService";
import { getUserFriendlyError } from "@Front/Utils/errorMessages";
import { isNetworkError, is429Error } from "@Front/Utils/errorClassifiers";
import { coalesceThrownApiErrorText } from "@Front/Utils/userFacingMessageFromApiClientError";
import { getActiveOfficeStore } from "@Front/Stores/ActiveOfficeStore";

export type Matrix = RolePermissionsMatrixApiResponse | null;

export function logMatrixResponse(
	_matrixResponse: RolePermissionsMatrixApiResponse,
	_permissionContextResponse: PermissionContextDto | null,
	_effectiveRole: string | null,
): void {
	/* intentional no-op: matrix response diagnostics removed from client logs */
}

export function logMatrixStateUpdate(
	_matrixResponse: RolePermissionsMatrixApiResponse,
	_permissionContextResponse: PermissionContextDto | null,
): void {
	/* intentional no-op */
}

export interface HandleRefreshErrorOptions {
	error: unknown;
	setMatrix: (value: Matrix) => void;
	setError: (value: string | null) => void;
	setLoading: (value: boolean) => void;
	setPermissionContext: (value: PermissionContextDto | null) => void;
}

function clearMatrixAndContext(opts: HandleRefreshErrorOptions): void {
	queueMicrotask(() => {
		opts.setMatrix(null);
		opts.setError(null);
		opts.setLoading(false);
		opts.setPermissionContext(null);
		getActiveOfficeStore().syncActiveOfficeFromContext(null);
	});
}

function handle401Or403(opts: HandleRefreshErrorOptions): void {
	clearMatrixAndContext(opts);
}

function handle429(opts: HandleRefreshErrorOptions): void {
	LoggerService.error("[RolePermissionsProvider] Rate limit (429) loading permissions matrix", {
		status: (opts.error as { response?: { status?: number } })?.response?.status,
	});
	queueMicrotask(() => {
		opts.setError(null);
		opts.setLoading(false);
	});
}

async function handleOtherError(opts: HandleRefreshErrorOptions): Promise<void> {
	const { error, setMatrix, setError, setLoading, setPermissionContext } = opts;
	const status = (error as { response?: { status?: number } })?.response?.status;
	const errorMessage = coalesceThrownApiErrorText(error);
	const networkError = isNetworkError(error);
	const isServerError = Boolean(status && status >= 500);

	LoggerService.error("[RolePermissionsProvider] Unable to load permissions matrix", {
		error: errorMessage,
		status,
		networkError,
		isServerError,
	});

	const friendly = await getUserFriendlyError(error);
	queueMicrotask(() => {
		setError(networkError || isServerError ? null : friendly.message);
		setMatrix(null);
		setLoading(false);
		setPermissionContext(null);
		getActiveOfficeStore().syncActiveOfficeFromContext(null);
	});
}

export async function handleRefreshError(opts: HandleRefreshErrorOptions): Promise<void> {
	const { error } = opts;
	const status = (error as { response?: { status?: number } })?.response?.status;

	if (status === 401) {
		handle401Or403(opts);
		return;
	}

	if (status === 403) {
		handle401Or403(opts);
		return;
	}

	if (status === 429 || is429Error(error)) {
		handle429(opts);
		return;
	}

	await handleOtherError(opts);
}

export interface LogGuestNotaryPermissionCheckOptions {
	normalizedResource: string;
	normalizedAction: string;
	normalizedScopedEntity: string | null;
	effectiveRole: string;
	actionNode: RolePermissionsMatrixApiEntry;
	matrix: Matrix;
	resourceNode: Record<string, RolePermissionsMatrixApiEntry> | undefined;
	phase: "BEFORE" | "AFTER";
	entry?: RolePermissionsMatrixApiEntry["roles"][string];
}

export function logGuestNotaryPermissionCheck(_opts: LogGuestNotaryPermissionCheckOptions): void {
	/* intentional no-op */
}
